/* Every add-to-cart path must POST Shopify's Ajax /cart/add.js: that emits the product_added_to_cart pixel Klaviyo triggers on. */

const ROOT_SELECTOR = '[data-bronte-pdp]';

let eventsPromise = null;

function themeEvents() {
  if (!eventsPromise) {
    eventsPromise = import('@shopify/events').catch(() => null);
  }
  return eventsPromise;
}

function routes() {
  // Theme is a script-scoped const in snippets/scripts.liquid, not a window property, so read it as a bare global.
  const configured = (typeof Theme !== 'undefined' && Theme.routes) || {};
  return {
    add: configured.cart_add_url || '/cart/add.js',
    cart: configured.cart_url || '/cart',
  };
}

function currency() {
  return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
}

function money(cents) {
  const value = Number(cents || 0) / 100;
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
      style: 'currency',
      currency: currency(),
      minimumFractionDigits: value % 1 === 0 ? 0 : 2,
    }).format(value);
  } catch (error) {
    return `£${value.toFixed(2)}`;
  }
}

/* Opening the drawer after an add is ours to do: Horizon only auto-opens under the editor-owned `auto_open_cart_drawer` setting. */
async function openCartDrawer() {
  try {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) return;

    if (!customElements.get('theme-drawer')) {
      await customElements.whenDefined('theme-drawer');
    }

    if (typeof drawer.open === 'function' && !drawer.isOpen) drawer.open();
  } catch (error) {
    // A drawer that will not open must never turn a completed add into a failed one.
  }
}

function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el.dataset.sectionId)
    .filter(Boolean)
    .join(',');
}

async function addLines(host, lines, sourceId, productId) {
  const cleaned = lines.filter((line) => line.id && line.quantity > 0);
  if (cleaned.length === 0) return false;

  const events = await themeEvents();
  const itemCount = cleaned.reduce((sum, line) => sum + line.quantity, 0);
  let deferred = null;

  if (events && events.CartLinesUpdateEvent) {
    deferred = events.CartLinesUpdateEvent.createPromise();
    host.dispatchEvent(
      new events.CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: cleaned.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
        promise: deferred.promise,
      })
    );
  }

  const path = routes();
  const sections = cartSectionIds();

  try {
    const response = await fetch(path.add, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: cleaned.map((line) => {
          const item = { id: Number(line.id), quantity: line.quantity };
          // Hidden line item properties (the pack's _bundle marker) ride through to the order.
          if (line.properties) item.properties = line.properties;
          return item;
        }),
        sections: sections || undefined,
      }),
    });
    const payload = await response.json();

    if (!response.ok) {
      if (events && events.CartErrorEvent) {
        host.dispatchEvent(
          new events.CartErrorEvent({
            error: payload.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: payload.description, errors: payload.errors },
          })
        );
      }
      if (deferred) deferred.reject(new Error(payload.message || 'Add to cart failed'));
      return false;
    }

    if (deferred && events) {
      const cart = await fetch(`${path.cart}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((res) => res.json());

      deferred.resolve({
        cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'bronte-pdp-main',
          sourceId,
          itemCount,
          productId,
          sections: payload.sections,
          didError: false,
        },
      });
    }

    await openCartDrawer();
    return true;
  } catch (error) {
    if (deferred) deferred.reject(error);
    return false;
  }
}

function initGallery(root) {
  const track = root.querySelector('[data-bpdp-stack]');
  const thumbs = Array.from(root.querySelectorAll('[data-bpdp-thumb]'));
  if (!track || thumbs.length < 2) return;

  const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const behavior = still ? 'auto' : 'smooth';
  let active = -1;

  // The strip's own scrollLeft, never scrollIntoView: that walks every scroll ancestor and would jump the page.
  function revealThumb(thumb) {
    const strip = thumb.parentElement;
    if (!strip || strip.scrollWidth <= strip.clientWidth) return;
    const target = thumb.offsetLeft - (strip.clientWidth - thumb.offsetWidth) / 2;
    strip.scrollTo({ left: Math.max(0, target), behavior });
  }

  function setActive(index) {
    if (index === active || index < 0 || index >= thumbs.length) return;
    active = index;
    thumbs.forEach((thumb, i) => {
      if (i === index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    revealThumb(thumbs[index]);
  }

  thumbs.forEach((thumb, index) => {
    thumb.addEventListener('click', () => {
      setActive(index);
      if (track.scrollWidth > track.clientWidth + 1) {
        track.scrollTo({ left: index * track.clientWidth, behavior });
      }
    });
  });

  let frame = 0;
  track.addEventListener(
    'scroll',
    () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const width = track.clientWidth;
        if (width > 0) setActive(Math.round(track.scrollLeft / width));
      });
    },
    { passive: true }
  );

  setActive(0);
}

/* Rects, not offsetLeft: the swatch's offsetParent is whatever is positioned above it, not the row. */
function initSwatches(root) {
  const row = root.querySelector('[data-bpdp-swatches]');
  if (!row) return;

  const current = row.querySelector('[aria-current="true"]');
  if (!current || row.scrollWidth <= row.clientWidth) return;

  const rowBox = row.getBoundingClientRect();
  const swatchBox = current.getBoundingClientRect();

  // Already fully visible means leave it alone, so the row stays left-anchored and the peek survives.
  if (swatchBox.left >= rowBox.left && swatchBox.right <= rowBox.right) return;

  const offset = row.scrollLeft + swatchBox.left - rowBox.left;
  row.scrollLeft = Math.max(0, offset - (row.clientWidth - swatchBox.width) / 2);
}

/* Add to cart is delegated off `document` at module scope, never bound inside init(): init() sets
   its done-flag before running the initialisers, so one throw upstream left the product form
   un-intercepted and a submit did a full-page POST to /cart/add. */
document.addEventListener(
  'submit',
  (event) => {
    const target = event.target;
    const form = target instanceof Element ? target.closest('.bronte-pdp__form') : null;
    if (!form) return;

    event.preventDefault();

    const root = form.closest(ROOT_SELECTOR);
    const variantField = root ? root.querySelector('[data-bpdp-variant-id]') : null;
    if (!root || !variantField) return;

    const button = root.querySelector('[data-bpdp-atc]');
    if (button) {
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
    }

    addLines(form, [{ id: variantField.value, quantity: 1 }], form.id, root.dataset.sectionId).finally(() => {
      if (button) {
        button.removeAttribute('aria-busy');
        button.disabled = false;
      }
    });
  },
  true
);

/* Prices come off the Liquid-rendered strings in the JSON, never re-formatted client-side:
   re-deriving would change currency shape under the shopper mid-visit. The sticky bar is
   re-resolved per update because initSticky portals it to <body>. */
function initVariantPicker(root) {
  const groups = Array.from(root.querySelectorAll('[data-bpdp-option-group]'));
  if (groups.length === 0) return;

  const source = root.querySelector('[data-bpdp-variants]');
  let variants = [];
  try {
    variants = JSON.parse(source.textContent) || [];
  } catch (error) {
    variants = [];
  }
  if (variants.length === 0) return;

  const idField = root.querySelector('[data-bpdp-variant-id]');
  const priceEl = root.querySelector('[data-bpdp-price]');
  const wasEl = root.querySelector('[data-bpdp-price-was]');

  function stickyPrice() {
    const sectionId = root.dataset.sectionId || '';
    const bar =
      root.querySelector('[data-bpdp-sticky]') ||
      document.querySelector(`body > [data-bpdp-sticky][data-section-id="${sectionId}"]`);
    return bar ? bar.querySelector('[data-bpdp-sticky-price]') : null;
  }

  function selectedValues() {
    const chosen = {};
    groups.forEach((group) => {
      const position = parseInt(group.dataset.optionPosition, 10);
      const checked = group.querySelector('[data-bpdp-option-value][aria-checked="true"]');
      if (checked && !Number.isNaN(position)) chosen[position] = checked.dataset.optionValue;
    });
    return chosen;
  }

  /* Options with a single value render no group, so they never appear in `chosen` and cannot disambiguate anything. */
  function resolveVariant(chosen) {
    return (
      variants.find((variant) => {
        const options = variant.options || [];
        return Object.keys(chosen).every((position) => options[Number(position) - 1] === chosen[position]);
      }) || null
    );
  }

  function updatePrice(variant) {
    if (!variant) return;

    if (variant.price_formatted) {
      if (priceEl) priceEl.textContent = variant.price_formatted;
      const sticky = stickyPrice();
      if (sticky) sticky.textContent = variant.price_formatted;
    }

    if (wasEl) {
      const show = typeof variant.compare_at_price === 'number' && variant.compare_at_price > variant.price;
      if (show && variant.compare_at_formatted) wasEl.textContent = variant.compare_at_formatted;
      wasEl.hidden = !show;
    }

    // The pack picker prices its own rows off this attribute.
    const unit = root.querySelector('[data-bpdp-unit-price]');
    if (unit && typeof variant.price === 'number') unit.dataset.bpdpUnitPrice = String(variant.price);
  }

  function updateAtc(variant) {
    const button = root.querySelector('[data-bpdp-atc]');
    if (!button) return;

    const available = variant ? variant.available !== false : false;
    const label = button.querySelector('[data-bpdp-atc-label]');

    button.disabled = !available;
    if (available) button.removeAttribute('aria-disabled');
    else button.setAttribute('aria-disabled', 'true');

    // Read the label off the section setting on the button, never off whatever the label currently says.
    if (label) label.textContent = available ? button.dataset.atcLabel || label.textContent : 'Sold out';
  }

  /* Show notify-me when the chosen variant cannot be bought. Hiding goes through resetBackInStock, which clears `required`. */
  function updateBackInStock(variant) {
    const wrap = root.querySelector('[data-bpdp-bis]');
    if (!wrap) return;
    const hide = variant ? variant.available !== false : true;
    wrap.hidden = hide;
    if (hide) resetBackInStock(root);
  }

  /* Reuses the thumb's own click handler rather than duplicating the gallery's state. */
  function selectMedia(mediaId) {
    if (!mediaId) return;
    const thumb = root.querySelector(`[data-bpdp-thumb][data-media-id="${mediaId}"]`);
    if (thumb) thumb.click();
  }

  /* Klaviyo's back-in-stock trigger and copied links read `?variant=`, so the address bar follows the pills. replaceState. */
  function syncUrl(variant) {
    if (!variant || !window.history || typeof history.replaceState !== 'function') return;
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url.toString());
    } catch (error) {
      // A location the URL parser will not take is not worth losing the picker over.
    }
  }

  function syncLabels() {
    groups.forEach((group) => {
      const block = group.closest('[data-bpdp-option-block]');
      const output = block ? block.querySelector('[data-bpdp-option-current]') : null;
      const checked = group.querySelector('[data-bpdp-option-value][aria-checked="true"]');
      if (output && checked) output.textContent = checked.dataset.optionValue || '';
    });
  }

  function apply(variant, fromShopper) {
    syncLabels();
    if (variant && idField) idField.value = variant.id;
    updatePrice(variant);
    updateAtc(variant);
    updateBackInStock(variant);
    if (variant && fromShopper) {
      selectMedia(variant.featured_media_id);
      syncUrl(variant);
    }
  }

  groups.forEach((group) => {
    const pills = Array.from(group.querySelectorAll('[data-bpdp-option-value]'));
    pills.forEach((pill) => {
      pill.addEventListener('click', () => {
        pills.forEach((other) => other.setAttribute('aria-checked', other === pill ? 'true' : 'false'));
        apply(resolveVariant(selectedValues()), true);
      });
    });
  });

  apply(resolveVariant(selectedValues()), false);
}

function initBundle(root) {
  const toggle = root.querySelector('[data-bpdp-bundle-toggle]');
  const panel = root.querySelector('[data-bpdp-panel]');
  if (!panel) return;

  if (toggle) {
    toggle.addEventListener('click', () => {
      const open = panel.dataset.open === 'true';
      if (open) {
        panel.dataset.open = 'false';
        toggle.setAttribute('aria-expanded', 'false');
        window.setTimeout(() => {
          if (panel.dataset.open !== 'true') panel.hidden = true;
        }, 400);
      } else {
        panel.hidden = false;
        requestAnimationFrame(() => {
          panel.dataset.open = 'true';
        });
        toggle.setAttribute('aria-expanded', 'true');
      }
    });
  }

  const picks = Array.from(panel.querySelectorAll('[data-bpdp-pick]'));
  const nudge = panel.querySelector('[data-bpdp-nudge]');
  const packButton = panel.querySelector('[data-bpdp-pack-add]');
  const barId = panel.querySelector('[data-bronte-progress]')?.dataset.bpbId;

  let tiers = [];
  const tierData = panel.querySelector('[data-bpdp-tiers]');
  if (tierData) {
    try {
      tiers = JSON.parse(tierData.textContent) || [];
    } catch (error) {
      tiers = [];
    }
  }
  tiers.sort((a, b) => a.pairs - b.pairs);

  // Projected pack totals assume extra pairs cost what this product costs, the honest assumption for a collection-fed picker.
  const unitPrice = Number(panel.dataset.bpdpUnitPrice || picks[0]?.dataset.price || 0);

  function state() {
    let pairs = 0;
    let subtotal = 0;
    picks.forEach((pick) => {
      const qty = Number(pick.dataset.qty || 0);
      pairs += qty;
      subtotal += qty * Number(pick.dataset.price || 0);
    });

    let reached = null;
    let next = null;
    tiers.forEach((tier) => {
      if (pairs >= tier.pairs) reached = tier;
      else if (!next) next = tier;
    });

    return { pairs, subtotal, reached, next };
  }

  // The action label is computed once here and handed to the sticky bar; nothing downstream re-derives a pack total.
  const listeners = [];
  const packLabel = (packButton && packButton.dataset.label) || 'Add Pack To Cart';
  let snapshot = { pairs: 0, total: 0, actionLabel: packLabel };

  function render() {
    const { pairs, subtotal, reached, next } = state();
    const saving = reached ? reached.saving : 0;
    const total = Math.max(0, subtotal - saving);
    const actionLabel = pairs > 0 ? `${packLabel}: ${money(total)}` : packLabel;

    snapshot = { pairs, total, actionLabel };

    if (window.BronteProgressBar && barId) {
      window.BronteProgressBar.update(barId, pairs);
    }

    if (packButton) {
      packButton.textContent = actionLabel;
      packButton.disabled = pairs === 0;
    }

    if (nudge) {
      if (next) {
        const needed = next.pairs - pairs;
        const targetTotal = Math.max(0, subtotal + needed * unitPrice - next.saving);
        const noun = needed === 1 ? 'pair' : 'pairs';
        nudge.textContent = `${pairs} of ${next.pairs} picked: add ${needed} more ${noun} to unlock ${money(targetTotal)}`;
      } else if (reached) {
        nudge.textContent = `${pairs} picked: you save ${money(saving)}`;
      } else {
        nudge.textContent = '';
      }
    }

    listeners.forEach((listener) => listener(snapshot));
  }

  picks.forEach((pick) => {
    const value = pick.querySelector('[data-bpdp-step-value]');
    pick.querySelectorAll('[data-bpdp-step]').forEach((step) => {
      step.addEventListener('click', () => {
        const delta = Number(step.dataset.bpdpStep || 0);
        const next = Math.max(0, Number(pick.dataset.qty || 0) + delta);
        pick.dataset.qty = String(next);
        if (value) value.textContent = String(next);
        render();
      });
    });
  });

  async function addPack() {
    const lines = picks
      .map((pick) => ({ id: pick.dataset.variantId, quantity: Number(pick.dataset.qty || 0) }))
      .filter((line) => line.quantity > 0);
    if (lines.length === 0) return false;

    // Same marker the Build Your Pack page sends: pack-{total pairs} on every sock line.
    const packPairs = lines.reduce((sum, line) => sum + line.quantity, 0);
    lines.forEach((line) => {
      line.properties = { _bundle: `pack-${packPairs}` };
    });

    return addLines(panel, lines, `bronte-pack-${root.dataset.sectionId}`, root.dataset.sectionId);
  }

  if (packButton) {
    packButton.addEventListener('click', async () => {
      if (snapshot.pairs === 0) return;
      packButton.setAttribute('aria-busy', 'true');
      packButton.disabled = true;
      await addPack();
      packButton.removeAttribute('aria-busy');
      packButton.disabled = false;
    });
  }

  // Published for the sticky bar. Absent on a product outside the bundle collection, which has no pack state.
  root.bpdpPack = {
    add: addPack,
    subscribe(listener) {
      listeners.push(listener);
      listener(snapshot);
    },
  };

  render();
}

/* Takes `required` off the email input. The input sits inside {% form 'product' %}, which has no
   novalidate: a hidden control still carrying `required` is validated, cannot be focused, and the
   browser silently refuses the submit before any submit handler runs. */
function resetBackInStock(root) {
  const wrap = root ? root.querySelector('[data-bpdp-bis]') : null;
  if (!wrap) return;
  const email = wrap.querySelector('[data-bpdp-bis-email]');
  const panel = wrap.querySelector('[data-bpdp-bis-panel]');
  const trigger = wrap.querySelector('[data-bpdp-bis-trigger]');
  const row = wrap.querySelector('[data-bpdp-bis-row]');
  const message = wrap.querySelector('[data-bpdp-bis-msg]');
  if (email) email.required = false;
  if (panel) panel.hidden = true;
  if (trigger) trigger.hidden = false;
  if (row) row.hidden = false;
  if (message) message.hidden = true;
}

/* Capture-phase, so it runs before the button's default action and therefore before validation. */
document.addEventListener(
  'click',
  (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-bpdp-atc], [data-bpdp-sticky-atc]') : null;
    if (!button) return;
    const sticky = button.closest('[data-bpdp-sticky]');
    const root = sticky
      ? document.querySelector(`${ROOT_SELECTOR}[data-section-id="${sticky.dataset.sectionId}"]`)
      : button.closest(ROOT_SELECTOR);
    const wrap = root ? root.querySelector('[data-bpdp-bis]') : null;
    if (wrap && wrap.hidden) resetBackInStock(root);
  },
  true
);

function initBackInStock(root) {
  const wrap = root.querySelector('[data-bpdp-bis]');
  if (!wrap) return;

  const key = wrap.dataset.klaviyoKey;
  const trigger = wrap.querySelector('[data-bpdp-bis-trigger]');
  const panel = wrap.querySelector('[data-bpdp-bis-panel]');
  const email = wrap.querySelector('[data-bpdp-bis-email]');
  const submit = wrap.querySelector('[data-bpdp-bis-submit]');
  const message = wrap.querySelector('[data-bpdp-bis-msg]');
  const row = wrap.querySelector('[data-bpdp-bis-row]');
  const variantField = root.querySelector('[data-bpdp-variant-id]');
  if (!trigger || !panel || !email) return;

  trigger.addEventListener('click', () => {
    trigger.hidden = true;
    panel.hidden = false;
    email.required = true;
    email.focus();
  });

  function report(ok) {
    if (!message) return;
    message.textContent = ok ? message.dataset.success || '' : message.dataset.error || '';
    message.dataset.state = ok ? 'success' : 'error';
    message.hidden = false;
    if (ok) hideRow();
  }

  function hideRow() {
    if (row) row.hidden = true;
    email.required = false;
  }

  async function subscribe() {
    if (!key || !variantField) return;
    if (!email.checkValidity()) {
      email.reportValidity();
      return;
    }
    if (submit) submit.disabled = true;

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(key)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: '2024-06-15' },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                profile: { data: { type: 'profile', attributes: { email: email.value.trim() } } },
                channels: ['EMAIL'],
              },
              relationships: {
                variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantField.value}` } },
              },
            },
          }),
        }
      );
      report(response.ok || response.status === 202);
    } catch (error) {
      report(false);
    }

    if (submit) submit.disabled = false;
  }

  if (submit) submit.addEventListener('click', subscribe);
  email.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      subscribe();
    }
  });
}

/* Portaled to <body>: a `position: fixed` element is trapped by any transformed ancestor. */
function initNamesake(root) {
  const trigger = root.querySelector('[data-bpdp-namesake-open]');
  const modal = root.querySelector('[data-bpdp-namesake]');
  if (!trigger || !modal) return;

  const sectionId = root.dataset.sectionId || '';
  const stale = document.querySelector(`body > [data-bpdp-namesake][data-section-id="${sectionId}"]`);
  if (stale && stale !== modal) stale.remove();
  if (modal.parentElement !== document.body) document.body.appendChild(modal);

  const dialog = modal.querySelector('[role="dialog"]');
  const closeButton = modal.querySelector('.bronte-namesake__close');
  let lastFocused = null;

  function open() {
    lastFocused = document.activeElement;
    modal.hidden = false;
    requestAnimationFrame(() => {
      modal.dataset.open = 'true';
    });
    if (closeButton) closeButton.focus();
  }

  function close() {
    if (modal.hidden) return;
    modal.dataset.open = 'false';
    window.setTimeout(() => {
      if (modal.dataset.open !== 'true') modal.hidden = true;
    }, 250);
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  }

  trigger.addEventListener('click', open);

  modal.querySelectorAll('[data-bpdp-namesake-close]').forEach((el) => {
    el.addEventListener('click', close);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') close();
  });

  modal.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab' || !dialog) return;
    const focusable = dialog.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
}

/* The mobile bottom bar and the desktop corner pill are the same element. It exists only while the buy column's
   own Add To Cart is off screen, and mirrors the pack whenever the picker HOLDS two pairs or more. */
function initSticky(root) {
  const bar = root.querySelector('[data-bpdp-sticky]');
  const realAtc = root.querySelector('[data-bpdp-atc]');
  const form = root.querySelector('.bronte-pdp__form');
  const title = root.querySelector('.bronte-pdp__title');
  if (!bar || !realAtc || !form) return;

  const sectionId = root.dataset.sectionId || '';
  const stale = document.querySelector(`body > [data-bpdp-sticky][data-section-id="${sectionId}"]`);
  if (stale && stale !== bar) stale.remove();
  if (bar.parentElement !== document.body) document.body.appendChild(bar);
  bar.hidden = false;

  const stickyAtc = bar.querySelector('[data-bpdp-sticky-atc]');
  const stickyLabel = bar.querySelector('[data-bpdp-sticky-label]');
  const singleLabel = stickyLabel ? stickyLabel.textContent.trim() : '';
  const pack = root.bpdpPack || null;
  let packMode = false;

  if (pack) {
    pack.subscribe((snapshot) => {
      packMode = snapshot.pairs >= 2;
      bar.dataset.mode = packMode ? 'pack' : 'single';
      if (stickyLabel) stickyLabel.textContent = packMode ? snapshot.actionLabel : singleLabel;
    });
  }

  if (stickyAtc) {
    stickyAtc.addEventListener('click', async () => {
      if (packMode && pack) {
        stickyAtc.setAttribute('aria-busy', 'true');
        stickyAtc.disabled = true;
        await pack.add();
        stickyAtc.removeAttribute('aria-busy');
        stickyAtc.disabled = false;
        return;
      }

      // Single mode defers to the real button's own state, so a sold-out product's sticky stays a no-op.
      if (realAtc.disabled) return;
      if (form.requestSubmit) form.requestSubmit();
      else realAtc.click();
    });
  }

  let passedTop = false;
  let atcInView = false;
  let footerInView = false;

  function apply() {
    const show = passedTop && !atcInView && !footerInView;
    bar.dataset.visible = show ? 'true' : 'false';
    bar.setAttribute('aria-hidden', show ? 'false' : 'true');
  }

  if (!('IntersectionObserver' in window)) return;

  if (title) {
    new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        passedTop = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        apply();
      },
      { threshold: 0 }
    ).observe(title);
  }

  new IntersectionObserver(
    (entries) => {
      atcInView = entries[0].isIntersecting;
      apply();
    },
    { threshold: 0 }
  ).observe(realAtc);

  const footer = document.querySelector('.section-footer') || document.querySelector('footer');
  if (footer) {
    new IntersectionObserver(
      (entries) => {
        footerInView = entries[0].isIntersecting;
        apply();
      },
      { threshold: 0 }
    ).observe(footer);
  }

  apply();
}

/* The review app's own anchor to #looxReviewsFrame resolves to a 0px scroll: the frame is an iframe that lays out
   late. Scroll the reviews section wrapper instead (above 990px `.page-wrapper` is the scroll container). */
function initRatingScroll(root) {
  const rating = root.querySelector('.bronte-pdp__rating');
  if (!rating) return;

  function target() {
    return (
      document.querySelector('.bronte-reviews') ||
      document.getElementById('looxReviewsFrame') ||
      document.querySelector('[id*="looxReviews"]')
    );
  }

  rating.addEventListener(
    'click',
    (event) => {
      const destination = target();
      if (!destination) return;

      event.preventDefault();
      event.stopPropagation();

      const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      destination.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'start' });

      if (!destination.hasAttribute('tabindex')) destination.setAttribute('tabindex', '-1');
      destination.focus({ preventScroll: true });
    },
    true
  );
}

/* One broken enhancement must not take the section down. Add to cart is delegated at module scope above. */
function init(root) {
  if (!root || root.dataset.bpdpInit === 'true') return;
  root.dataset.bpdpInit = 'true';

  [initGallery, initSwatches, initVariantPicker, initBundle, initBackInStock, initNamesake, initSticky, initRatingScroll].forEach((step) => {
    try {
      step(root);
    } catch (error) {
      console.warn(`[bronte-pdp-main] ${step.name} failed:`, error);
    }
  });
}

function initAll(scope) {
  (scope || document).querySelectorAll(ROOT_SELECTOR).forEach(init);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll());
} else {
  initAll();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) initAll(event.target);
});
